import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment-timezone";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Filter,
  Search,
  X,
} from "lucide-react";
import DateRangePicker from "src/components/sector-momentum/DateRangePicker";
import Filters, { FiltersFieldProps } from "src/components/dashboard/Filters";
import { SearchContext } from "src/common/search";
import { fetchWrapper } from "src/helpers";
import { toTitleCase } from "src/helpers/api/sharedFunction";
import {
  formatSelectionLabel,
  toMonthDayDate,
  toMonthId,
} from "src/helpers/performance-month";
import { Universe } from "src/hooks/useCustomUniverses";

type FiltersData = Record<string, { label: string; value: string }[]>;

interface SearchData {
  symbol: string;
  company_name: string;
  indicesname?: string;
}

export const defaultFilters: FiltersFieldProps = {
  sector: [],
  segment: [],
  near200SMA: [],
  fibLevel: [],
  stockTrend: [],
  machineLogic: "",
  propLogic: "",
  risk_profile: [],
};

interface PerformanceControlsProps {
  token: string;
  universes: Universe[];
  hasCustomUniverseAccess: boolean;
  initialFromDate: string;
  initialToDate: string;
  onFiltersChange: (filters: {
    fromDate: string;
    toDate: string;
    selectedUniverses: string[];
    selectedCustomUniverses: string[];
    appliedFilters: FiltersFieldProps;
    searchSymbol: string;
  }) => void;
}

interface HeatmapDataPoint {
  date: string;
  avg_peak_return: number;
  avg_current_return: number;
}

interface PerformanceHeatmapProps {
  heatmapData: HeatmapDataPoint[];
  heatmapDate?: string | null;
  onDateSelect?: (date: string) => void;
  /** Whole-month drill-down, mutually exclusive with `heatmapDate`. Id format: "MMM-YYYY". */
  selectedMonth?: string | null;
  onMonthSelect?: (monthId: string) => void;
  /**
   * Identifies the picked date-range window. Changing it re-centers the
   * scroll on the latest month; leaving it the same (e.g. only a filter
   * changed) preserves wherever the user has scrolled to.
   */
  dateRangeKey?: string;
  viewMode: "peak" | "current";
  onViewModeChange: (mode: "peak" | "current") => void;
  isLoading?: boolean;
  /** Rendered to the right of the heatmap grid on wide screens. */
  sideSlot?: React.ReactNode;
}

const PerformanceHeatmap: React.FC<PerformanceHeatmapProps> = ({
  heatmapData,
  heatmapDate,
  onDateSelect,
  selectedMonth,
  onMonthSelect,
  dateRangeKey,
  viewMode,
  onViewModeChange,
  isLoading,
  sideSlot,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isHeatmapScrollable, setIsHeatmapScrollable] = useState(false);
  /** Which side still has months left to reveal, so a dead arrow is not shown. */
  const [scrollEdges, setScrollEdges] = useState({ left: false, right: false });

  /** The active drill-down, shown as a clearable chip in the header. */
  const selectionLabel = formatSelectionLabel(heatmapDate, selectedMonth);
  const clearSelection = () => {
    if (heatmapDate) onDateSelect?.(heatmapDate);
    else if (selectedMonth) onMonthSelect?.(selectedMonth);
  };

  // Dynamically generate months from heatmapData - Constrained to 1 year
  const dynamicMonths = useMemo(() => {
    if (heatmapData.length === 0) return [];

    // Show all months present in heatmapData
    const dates = heatmapData.map((d) => moment(d.date));
    const start = moment.min(dates).clone().startOf("month");
    const end = moment.max(dates).clone().endOf("month");

    const result = [];
    const curr = start.clone();
    while (curr.isSameOrBefore(end)) {
      result.push({
        id: toMonthId(curr),
        label: curr.format("MMM"),
        year: curr.format("'YY"),
      });
      curr.add(1, "month");
    }
    return result;
  }, [heatmapData]);

  const heatmapGridData = useMemo(() => {
    const data: Record<string, Record<number, number>> = {};
    heatmapData.forEach((item) => {
      const date = moment(item.date);
      const key = toMonthId(date);
      const day = date.date();
      if (!data[key]) data[key] = {};
      data[key][day] =
        viewMode === "peak" ? item.avg_peak_return : item.avg_current_return;
    });
    return data;
  }, [heatmapData, viewMode]);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setIsHeatmapScrollable(maxScroll > 1);
    setScrollEdges((prev) => {
      const left = maxScroll > 1 && el.scrollLeft > 1;
      const right = maxScroll > 1 && el.scrollLeft < maxScroll - 1;
      return prev.left === left && prev.right === right
        ? prev
        : { left, right };
    });
  };

  const handleScroll = () => {
    const el = scrollRef.current;
    if (el && el.scrollWidth - el.clientWidth <= 1) {
      el.scrollLeft = 0;
    }
    updateScrollState();
  };

  /**
   * The months run sideways, so a vertical wheel gesture is turned into a
   * horizontal one — that is what replaces the old arrow buttons. At either end
   * the event is left alone so the page keeps scrolling normally.
   */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleWheel = (event: WheelEvent) => {
      if (event.ctrlKey) return;

      const maxScroll = el.scrollWidth - el.clientWidth;
      if (maxScroll <= 1) return;

      // Trackpads already send a horizontal delta; only translate the vertical.
      const delta =
        Math.abs(event.deltaX) > Math.abs(event.deltaY)
          ? event.deltaX
          : event.deltaY;
      if (delta === 0) return;

      const next = Math.min(maxScroll, Math.max(0, el.scrollLeft + delta));
      if (next === el.scrollLeft) return;

      event.preventDefault();
      el.scrollLeft = next;
    };

    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, []);

  /**
   * Jumping to the latest month is only wanted for a genuinely new window to
   * look at — the first load, or the date-range picker moving elsewhere.
   * Refetches triggered by a filter/universe change reuse the same
   * `dateRangeKey` and must leave the scroll position exactly where the user
   * left it.
   */
  const hasInitializedScrollRef = useRef(false);
  const lastDateRangeKeyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const el = scrollRef.current;
    // Nothing rendered yet (first load, or a genuinely empty result) is the
    // only case worth bailing on — a stale-data refetch keeps the previous
    // months on screen and scrollable, so it must not be treated the same.
    if (!el || dynamicMonths.length === 0) {
      requestAnimationFrame(() => setIsHeatmapScrollable(false));
      return;
    }

    const rangeChanged =
      !hasInitializedScrollRef.current ||
      (dateRangeKey !== undefined &&
        dateRangeKey !== lastDateRangeKeyRef.current);
    hasInitializedScrollRef.current = true;
    lastDateRangeKeyRef.current = dateRangeKey;

    // Open on the most recent month — that is the data users look at first.
    const applyLayout = (shouldJumpToLatest: boolean) => {
      requestAnimationFrame(() => {
        const maxScroll = el.scrollWidth - el.clientWidth;
        if (shouldJumpToLatest && maxScroll > 0) {
          el.scrollLeft = maxScroll;
        }
        updateScrollState();
      });
    };

    applyLayout(rangeChanged);
    const resizeObserver = new ResizeObserver(() => applyLayout(false));
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
  }, [dynamicMonths, isLoading, viewMode, dateRangeKey]);

  const scrollHeatmap = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    // Roughly three months per click, but never more than the visible width.
    const step = Math.min(el.clientWidth * 0.8, 360);
    el.scrollBy({
      left: direction === "left" ? -step : step,
      behavior: "smooth",
    });
  };

  function getDateColor(value: number): string {
    // Elegant, harmonious palette
    if (value >= 15) return "bg-emerald-600/90";
    if (value >= 8) return "bg-emerald-500/80";
    if (value >= 3) return "bg-emerald-400/70";
    if (value > 0) return "bg-emerald-200/60 dark:bg-emerald-500/20";

    if (value <= -15) return "bg-rose-600/90";
    if (value <= -8) return "bg-rose-500/80";
    if (value <= -3) return "bg-rose-400/70";
    if (value < 0) return "bg-rose-200/60 dark:bg-rose-500/20";

    return "bg-gray-100 dark:bg-white/5";
  }

  //   function getDateColor(value: number): string {
  //   if (value >= 10) return "bg-emerald-600";
  //   if (value >= 5) return "bg-emerald-500";
  //   if (value >= 0) return "bg-emerald-300";
  //   if (value >= -5) return "bg-orange-300";
  //   if (value >= -10) return "bg-orange-400";
  //   return "bg-red-600";
  // }

  const heatmapCard = (
    <div className="group relative flex h-full min-h-[10.5rem] w-full min-w-0 flex-col overflow-hidden bg-white dark:bg-[#121419]">
      <div className="flex shrink-0 items-center justify-between gap-2 px-1 py-1">
        <div className="flex min-w-0 items-center gap-2">
          <h3 className="text-sm font-medium text-[#3D3D3D] dark:text-white">
            Daily Returns
          </h3>

          {/*
            Everything below the heatmap is filtered to this selection, so it
            has to be visible — and clearable — without hunting for the cell
            that is highlighted somewhere off-screen.
          */}
          {selectionLabel && (
            <button
              type="button"
              onClick={clearSelection}
              title="Clear selection"
              className="flex min-w-0 items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-600 transition-colors hover:bg-emerald-500/25 dark:text-emerald-400 cursor-pointer"
            >
              <span className="truncate">{selectionLabel}</span>
              <X className="h-3 w-3 shrink-0" />
            </button>
          )}
        </div>

        <div className="flex shrink-0 items-center bg-gray-100 dark:bg-[#151921] p-0.5 rounded-md h-6">
          <button
            onClick={() => onViewModeChange("peak")}
            className={`px-2 h-full text-[10px] font-semibold rounded transition-all duration-200 cursor-pointer ${
              viewMode === "peak"
                ? "bg-white text-emerald-600 shadow-xs dark:text-white dark:bg-emerald-600"
                : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            }`}
          >
            Peak Return
          </button>
          <button
            onClick={() => onViewModeChange("current")}
            className={`px-2 h-full text-[10px] font-semibold rounded transition-all duration-200 cursor-pointer ${
              viewMode === "current"
                ? "bg-white text-emerald-600 shadow-xs dark:text-white dark:bg-emerald-600"
                : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            }`}
          >
            Current Return
          </button>
        </div>
      </div>

      <div className="relative flex min-h-0 flex-1">
        {scrollEdges.left && (
          <button
            type="button"
            aria-label="Scroll to earlier months"
            onClick={() => scrollHeatmap("left")}
            className="absolute left-1 top-1/2 z-30 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full border border-[#d0d4e4] bg-white/95 text-gray-400 shadow-xs backdrop-blur-xs transition-colors hover:text-emerald-500 dark:border-[#394564] dark:bg-[#121419]/95 cursor-pointer"
          >
            <ChevronRight className="h-3.5 w-3.5 rotate-180" />
          </button>
        )}

        {scrollEdges.right && (
          <button
            type="button"
            aria-label="Scroll to later months"
            onClick={() => scrollHeatmap("right")}
            className="absolute right-1 top-1/2 z-30 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full border border-[#d0d4e4] bg-white/95 text-gray-400 shadow-xs backdrop-blur-xs transition-colors hover:text-emerald-500 dark:border-[#394564] dark:bg-[#121419]/95 cursor-pointer"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        )}

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className={`heatmap-scroll-container no-scrollbar flex min-h-0 w-full flex-col justify-center overflow-y-hidden px-1 py-1 ${
            isHeatmapScrollable ? "overflow-x-auto" : "overflow-x-hidden"
          }`}
        >
          {isLoading && dynamicMonths.length === 0 ? (
            // Only the very first load (nothing to show yet) gets the full
            // skeleton. A refetch triggered by a filter change keeps the
            // months and day cells already on screen — React reconciles them
            // by the same keys below, so a color update is an in-place
            // className swap, not a teardown: no reset scroll, no re-mount.
            <div className="mx-auto flex w-max flex-nowrap gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={`skeleton-month-${i}`}
                  className="flex-shrink-0 animate-pulse"
                >
                  {/* Same height, margin and cell count as the real header/grid
                      below — a mismatched placeholder is what made the swap
                      to real data visibly jump instead of landing in place. */}
                  <div className="mb-1.5 h-5 w-14 bg-gray-100 dark:bg-white/5 rounded mx-auto" />
                  <div className="grid grid-cols-6 gap-[3px] p-0.5">
                    {Array.from({ length: 31 }).map((_, j) => (
                      <div
                        key={`skeleton-day-${j}`}
                        className="w-[14px] h-[14px] bg-gray-100 dark:bg-white/5 rounded-[2px]"
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : dynamicMonths.length > 0 ? (
            <div
              className={`mx-auto flex w-max flex-nowrap gap-4 transition-opacity duration-200 ${
                isLoading ? "opacity-60" : "opacity-100"
              }`}
            >
              {dynamicMonths.map((m) => {
                // A month key only exists once at least one day has a value,
                // so its presence is the "has data" test.
                const monthCells = heatmapGridData[m.id];
                const hasMonthData = monthCells !== undefined;
                const isMonthSelected = selectedMonth === m.id;

                return (
                  <div key={m.id} className="flex-shrink-0">
                    <button
                      type="button"
                      disabled={!hasMonthData}
                      aria-pressed={isMonthSelected}
                      onClick={() => onMonthSelect?.(m.id)}
                      title={
                        hasMonthData
                          ? `${isMonthSelected ? "Clear" : "Select"} all of ${m.label}${m.year}`
                          : "No data"
                      }
                      className={`mb-1.5 flex w-full items-baseline justify-center gap-1 rounded text-sm font-medium tracking-wide transition-colors ${
                        hasMonthData
                          ? "cursor-pointer hover:text-emerald-500"
                          : "cursor-default"
                      } ${
                        isMonthSelected
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      <span>{m.label}</span>
                      <span className="text-[12px]">{m.year}</span>
                    </button>
                    <div
                      className={`grid grid-cols-6 gap-[3px] rounded-[3px] p-0.5 transition-all duration-200 ${
                        isMonthSelected
                          ? "ring-1 ring-emerald-500 dark:ring-emerald-400"
                          : ""
                      }`}
                    >
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(
                        (day) => {
                          const value = monthCells?.[day];
                          const dateStr = toMonthDayDate(day, m.id);
                          const isSelected = heatmapDate === dateStr;

                          return (
                            <div
                              key={day}
                              onClick={() => {
                                if (value !== undefined && onDateSelect) {
                                  onDateSelect(dateStr);
                                }
                              }}
                              className={`w-[14px] h-[14px] rounded-[2px] transition-all duration-200 ${
                                value !== undefined
                                  ? getDateColor(value)
                                  : "bg-gray-50 dark:bg-white/5"
                              } ${value !== undefined ? "cursor-pointer" : ""} ${
                                isSelected
                                  ? "ring-1 ring-emerald-500 dark:ring-emerald-400 scale-110 z-20"
                                  : "hover:scale-110 hover:z-10"
                              }`}
                              title={
                                value !== undefined
                                  ? `${day} ${m.label}${m.year}: ${value.toFixed(2)}%`
                                  : "No data"
                              }
                            />
                          );
                        },
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="w-full min-w-full py-8 flex flex-col items-center justify-center gap-2">
              <Filter className="w-8 h-8 text-gray-200 dark:text-gray-800" />
              <p className="text-gray-400 dark:text-gray-500  text-sm">
                No performance data available for this selection
              </p>
            </div>
          )}
        </div>
      </div>

      {/* eslint-disable-next-line react/no-unknown-property */}
      <style jsx>{`
        .heatmap-scroll-container.no-scrollbar::-webkit-scrollbar,
        .no-scrollbar::-webkit-scrollbar {
          display: none;
          width: 0;
          height: 0;
        }
        .heatmap-scroll-container.no-scrollbar,
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );

  if (!sideSlot) {
    return heatmapCard;
  }

  return (
    <div className="flex flex-col gap-2 xl:flex-row xl:items-stretch xl:h-[11.5rem] w-full">
      <div className="xl:w-[70%] min-w-0 xl:h-full xl:min-h-0">
        {heatmapCard}
      </div>
      <div className="xl:w-[30%] min-w-0 xl:h-full xl:min-h-0">{sideSlot}</div>
    </div>
  );
};

export const PerformanceControls: React.FC<PerformanceControlsProps> = ({
  token,
  universes,
  hasCustomUniverseAccess,
  onFiltersChange,
  initialFromDate,
  initialToDate,
}) => {
  // The dashboard owns the persisted filter selection; this page only borrows it.
  const { appliedFiltersStore, isFiltersLoaded } = useContext(SearchContext);
  const [selectedUniverses, setSelectedUniverses] = useState<string[]>([
    "Derivatives",
  ]);
  const [selectedCustomUniverses, setSelectedCustomUniverses] = useState<
    string[]
  >([]);
  const [showCustomDropdown, setShowCustomDropdown] = useState(false);
  const [fromDate, setFromDate] = useState(initialFromDate);
  const [toDate, setToDate] = useState(initialToDate);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filtersData, setFiltersData] = useState<FiltersData>({});
  const [selectedFilters, setSelectedFilters] =
    useState<FiltersFieldProps>(defaultFilters);
  const [appliedFilters, setAppliedFilters] =
    useState<FiltersFieldProps>(defaultFilters);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchSymbol, setSearchSymbol] = useState("");
  const [autoComplete, setAutoComplete] = useState(false);
  const [filteredData, setFilteredData] = useState<SearchData[]>([]);
  const [hasMounted, setHasMounted] = useState(false);
  /** Blocks the first fetch until the inherited selection is in place. */
  const [hasInheritedFilters, setHasInheritedFilters] = useState(false);

  const searchRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const universeScrollRef = useRef<HTMLDivElement>(null);
  const [isUniverseScrollable, setIsUniverseScrollable] = useState(false);
  const [dropdownCoords, setDropdownCoords] = useState({ top: 0, left: 0 });

  useEffect(() => {
    setHasMounted(true);
  }, []);

  /**
   * Adopt the dashboard's stored selection, and re-adopt it whenever that store
   * changes — that is what makes a filter edited on the dashboard show up here.
   * Nothing in this component writes back to the store, so edits made on this
   * page stay local and are gone again on the next load.
   */
  useEffect(() => {
    if (!isFiltersLoaded) return;
    const inherited = { ...defaultFilters, ...appliedFiltersStore };
    setSelectedFilters(inherited);
    setAppliedFilters(inherited);
    setHasInheritedFilters(true);
  }, [isFiltersLoaded, appliedFiltersStore]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowCustomDropdown(false);
      }
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setAutoComplete(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const checkScrollable = () => {
      if (universeScrollRef.current) {
        const { scrollWidth, clientWidth } = universeScrollRef.current;
        setIsUniverseScrollable(scrollWidth > clientWidth);
      }
    };
    checkScrollable();
    window.addEventListener("resize", checkScrollable);
    return () => window.removeEventListener("resize", checkScrollable);
  }, [hasMounted, universes, selectedUniverses]);

  useEffect(() => {
    if (showCustomDropdown) {
      const handleScroll = (e: Event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(e.target as Node)
        ) {
          setShowCustomDropdown(false);
        }
      };
      window.addEventListener("scroll", handleScroll, true);
      return () => window.removeEventListener("scroll", handleScroll, true);
    }
  }, [showCustomDropdown]);

  useEffect(() => {
    getFilterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Holding the first emit back keeps the page from fetching once with an
    // empty selection and again with the inherited one.
    if (!hasInheritedFilters) return;
    onFiltersChange({
      fromDate,
      toDate,
      selectedUniverses,
      selectedCustomUniverses,
      appliedFilters,
      searchSymbol,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasInheritedFilters,
    fromDate,
    toDate,
    selectedUniverses,
    selectedCustomUniverses,
    appliedFilters,
    searchSymbol,
  ]);

  useEffect(() => {
    if (universeScrollRef.current) {
      const activeBtn = universeScrollRef.current.querySelector(
        '[data-active="true"]',
      );
      if (activeBtn) {
        activeBtn.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  }, [selectedUniverses, selectedCustomUniverses]);

  const getFilterData = async () => {
    try {
      const response = await fetchWrapper.get(
        "/api/dashboard/filters",
        undefined,
        {
          Authorization: `Bearer ${token}`,
        },
      );
      setFiltersData(response?.data || {});
    } catch (error) {
      console.error("Failed to fetch filters:", error);
    }
  };

  const scrollUniverse = (direction: "left" | "right") => {
    if (universeScrollRef.current) {
      const scrollAmount = 200;
      universeScrollRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const handleSearchChange = async (query: string) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setAutoComplete(false);
      setFilteredData([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      try {
        const filterParams: Record<string, string> = {};
        Object.entries(appliedFilters).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            if (value.length > 0) filterParams[key] = value.join(",");
          } else if (value !== "" && value !== null && value !== undefined) {
            filterParams[key] = value;
          }
        });
        const response = await fetchWrapper.get(
          `/api/dashboard/company_info?name=${encodeURIComponent(query)}`,
          undefined,
          { Authorization: `Bearer ${token}` },
        );

        setFilteredData(response.data || []);
        setAutoComplete(true);
      } catch (err) {
        console.error("Search failed:", err);
      }
    }, 500);
  };

  const handleSelectSymbol = (stock: SearchData) => {
    setSearchSymbol(stock.symbol);
    setSearchQuery(stock.symbol);
    setAutoComplete(false);
    // Make sure the picked symbol is actually visible under the current
    // selection instead of silently returning an empty page.
    if (stock.indicesname && !selectedUniverses.includes(stock.indicesname)) {
      setSelectedUniverses([stock.indicesname]);
      setSelectedCustomUniverses([]);
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchSymbol("");
    setAutoComplete(false);
  };

  const toggleIndexUniverse = (universe: string) => {
    setSelectedUniverses((prev) =>
      prev.includes(universe)
        ? prev.filter((name) => name !== universe)
        : [...prev, universe],
    );
  };

  const toggleCustomUniverse = (universe: string) => {
    setSelectedCustomUniverses((prev) =>
      prev.includes(universe)
        ? prev.filter((name) => name !== universe)
        : [...prev, universe],
    );
  };

  const clearUniverseSelection = () => {
    setSelectedUniverses([]);
    setSelectedCustomUniverses([]);
  };

  const handleStartDateChange = (date: Date) => {
    let newFrom = moment(date);
    let newTo = moment(toDate);
    const maxDate = moment().subtract(1, "days");

    if (newTo.diff(newFrom, "days") < 7) {
      newTo = newFrom.clone().add(7, "days");
      if (newTo.isAfter(maxDate, "day")) {
        newTo = maxDate.clone();
        newFrom = newTo.clone().subtract(7, "days");
      }
      setToDate(newTo.format("YYYY-MM-DD"));
    }
    setFromDate(newFrom.format("YYYY-MM-DD"));
  };

  const handleEndDateChange = (date: Date) => {
    let newTo = moment(date);
    let newFrom = moment(fromDate);
    const maxDate = moment().subtract(1, "days");

    if (newTo.isAfter(maxDate, "day")) {
      newTo = maxDate.clone();
    }

    if (newTo.diff(newFrom, "days") < 7) {
      newFrom = newTo.clone().subtract(7, "days");
      setFromDate(newFrom.format("YYYY-MM-DD"));
    }
    setToDate(newTo.format("YYYY-MM-DD"));
  };

  const toggleFilterModal = () => {
    setIsFilterModalOpen(!isFilterModalOpen);
  };

  const resetAllFilters = () => {
    setSelectedFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
  };

  const applyFilters = () => {
    setAppliedFilters(selectedFilters);
    setIsFilterModalOpen(false);
  };

  /**
   * One per active filter group, matching the dashboard's count exactly — the
   * selection is shared, so the badge has to read the same on both pages. The
   * boolean check must be explicit: the inherited store carries `false` entries
   * for every unchecked toggle, and those are not applied filters.
   */
  const getAppliedFilterCount = (filters: FiltersFieldProps) => {
    let count = 0;
    Object.values(filters).forEach((value) => {
      if (Array.isArray(value)) {
        if (value.length > 0) count += 1;
      } else if (typeof value === "string" && value.trim() !== "") {
        count += 1;
      } else if (typeof value === "boolean" && value === true) {
        count += 1;
      }
    });
    return count;
  };

  const totalAppliedFilters = getAppliedFilterCount(appliedFilters);
  const totalSelectedUniverses =
    selectedUniverses.length + selectedCustomUniverses.length;
  const baseUniverses = [
    "Derivatives",
    "Nifty 500",
    "Nifty Micro 250",
    "Others",
    "Penny Stocks",
  ];

  return (
    <>
      <div className="flex flex-wrap items-center justify-end gap-2 min-w-0">
        {/* Filter & Search */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={toggleFilterModal}
            className="h-8 w-8 relative bg-gray-100 dark:bg-[#151921] rounded-md flex items-center justify-center hover:bg-emerald-500/10 transition-all duration-300 group cursor-pointer"
          >
            {totalAppliedFilters > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-emerald-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center border border-white dark:border-[#0B0E14] shadow-xs">
                {totalAppliedFilters}
              </span>
            )}
            <Filter className="w-3.5 h-3.5 text-gray-400 group-hover:text-emerald-500 transition-colors" />
          </button>

          <div className="relative w-36 sm:w-44" ref={searchRef}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search symbol..."
              className="h-8 w-full pl-8 pr-7 bg-gray-100 dark:bg-[#151921] rounded-md text-xs text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-emerald-500/50 transition-all duration-300"
            />
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
            {autoComplete && filteredData.length > 0 && (
              <div className="absolute top-full left-0 mt-1 w-full bg-white dark:bg-[#1A1F29] border border-gray-200 dark:border-[#2D3442] rounded-md shadow-xl z-[60] overflow-hidden py-1 max-h-60 overflow-y-auto">
                {filteredData.map((stock, idx) => (
                  <button
                    key={`${stock.symbol}-${idx}`}
                    onClick={() => handleSelectSymbol(stock)}
                    className="w-full px-2 py-1 text-left hover:bg-emerald-500/10 transition-colors flex flex-col"
                  >
                    <span className="text-xs font-bold text-gray-900 dark:text-white">
                      {stock.symbol}
                    </span>
                    <span className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                      {stock.company_name}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Stock Universe Pills */}
        <div className="relative flex items-center gap-1 max-w-full sm:max-w-md xl:max-w-lg group/universe flex-shrink min-w-0">
          {isUniverseScrollable && (
            <button
              onClick={() => scrollUniverse("left")}
              className="cursor-pointer p-0.5 rounded-full hover:bg-gray-100 dark:hover:bg-white/5 transition-colors text-gray-400 hover:text-emerald-500 z-10"
            >
              <ChevronRight className="w-3.5 h-3.5 rotate-180" />
            </button>
          )}

          <div
            className="flex overflow-x-auto flex-nowrap items-center gap-1.5 no-scrollbar scroll-smooth py-0.5"
            ref={universeScrollRef}
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {baseUniverses.map((uni) => {
              const isActive = selectedUniverses.includes(uni);
              return (
                <button
                  key={uni}
                  data-active={isActive}
                  role="checkbox"
                  aria-checked={isActive}
                  onClick={() => toggleIndexUniverse(uni)}
                  className={`h-8 px-3 rounded-md text-xs font-medium transition-all duration-200 whitespace-nowrap flex-shrink-0 flex items-center cursor-pointer ${
                    isActive
                      ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                      : "bg-gray-100 dark:bg-[#151921] text-gray-600 dark:text-gray-400 hover:text-emerald-500"
                  }`}
                >
                  {uni}
                </button>
              );
            })}

            {hasMounted && hasCustomUniverseAccess && (
              <div className="flex-shrink-0" ref={dropdownRef}>
                <button
                  data-active={selectedCustomUniverses.length > 0}
                  onClick={(e) => {
                    if (!showCustomDropdown) {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setDropdownCoords({
                        top: rect.bottom,
                        left: rect.left,
                      });
                    }
                    setShowCustomDropdown(!showCustomDropdown);
                  }}
                  className={`cursor-pointer h-8 px-3 rounded-md text-xs font-medium transition-all duration-200 whitespace-nowrap flex items-center gap-1.5 ${
                    selectedCustomUniverses.length > 0
                      ? "bg-indigo-500/15 text-indigo-600 dark:text-indigo-400"
                      : "bg-gray-100 dark:bg-[#151921] text-gray-600 dark:text-gray-400 hover:text-indigo-500"
                  }`}
                >
                  {selectedCustomUniverses.length === 1
                    ? toTitleCase(selectedCustomUniverses[0])
                    : "Custom"}
                  {selectedCustomUniverses.length > 1 && (
                    <span className="px-1.5 rounded-full bg-indigo-500 text-white text-[9px] font-bold leading-3">
                      {selectedCustomUniverses.length}
                    </span>
                  )}
                  <ChevronDown
                    className={`w-3.5 h-3.5 transition-transform duration-300 ${showCustomDropdown ? "rotate-180" : ""}`}
                  />
                </button>

                {showCustomDropdown && (
                  <div
                    className="fixed w-48 bg-white dark:bg-[#1A1F29] border border-gray-200 dark:border-[#2D3442] rounded-md shadow-xl z-50 overflow-hidden py-1"
                    style={{
                      top: dropdownCoords.top + 6,
                      left: dropdownCoords.left,
                    }}
                  >
                    {universes.length > 0 ? (
                      universes.map((u) => {
                        const isChecked = selectedCustomUniverses.includes(
                          u.name,
                        );
                        return (
                          <button
                            key={u.id}
                            role="checkbox"
                            aria-checked={isChecked}
                            onClick={() => toggleCustomUniverse(u.name)}
                            className={`w-full px-3 py-1.5 text-left text-xs flex items-center gap-2 hover:bg-indigo-500/10 hover:text-indigo-500 transition-colors ${
                              isChecked
                                ? "text-indigo-500 font-semibold"
                                : "dark:text-gray-300"
                            }`}
                          >
                            <span
                              className={`w-3 h-3 flex-shrink-0 rounded-[3px] border flex items-center justify-center transition-colors ${
                                isChecked
                                  ? "bg-indigo-500 border-indigo-500"
                                  : "border-gray-300 dark:border-[#2D3442]"
                              }`}
                            >
                              {isChecked && (
                                <Check className="w-2 h-2 text-white" />
                              )}
                            </span>
                            <span className="truncate">
                              {toTitleCase(u.name)}
                            </span>
                          </button>
                        );
                      })
                    ) : (
                      <div className="px-3 py-1.5 text-xs text-gray-500">
                        No custom universes
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {totalSelectedUniverses > 0 && (
              <button
                onClick={clearUniverseSelection}
                title="Clear universe selection (shows all universes)"
                className="h-8 px-2 flex-shrink-0 rounded-md text-xs font-medium text-gray-400 hover:text-rose-500 transition-colors cursor-pointer flex items-center gap-1"
              >
                <X className="w-3.5 h-3.5" />
                Clear
              </button>
            )}

            {totalSelectedUniverses === 0 && (
              <span className="flex-shrink-0 text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap px-1">
                Showing all universes
              </span>
            )}
          </div>

          {isUniverseScrollable && (
            <button
              onClick={() => scrollUniverse("right")}
              className="p-0.5 rounded-full hover:bg-gray-100 dark:hover:bg-white/5 transition-colors text-gray-400 hover:text-emerald-500 z-10"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* DateRangePicker */}
        {/* Borders are stripped page-side only — DateRangePicker is shared. */}
        <div className="flex-shrink-0 w-[14rem] sm:w-[15rem] [&>div]:w-full [&>div]:gap-1.5 [&>div>div]:w-full [&>div>div>div]:!w-full [&_input]:!w-full [&_input]:!h-8 [&_input]:!text-xs [&_input]:!border-0 [&_input]:!bg-gray-100 dark:[&_input]:!bg-[#151921]">
          <DateRangePicker
            page={"performance_heatmap"}
            startDate={moment(fromDate).toDate()}
            endDate={moment(toDate).toDate()}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
            minDate={moment().subtract(3, "years").toDate()}
            maxDate={moment().subtract(1, "days").toDate()}
          />
        </div>
      </div>

      {isFilterModalOpen && (
        <Filters
          toggleFilterModal={toggleFilterModal}
          resetAllFilters={resetAllFilters}
          applyFilters={applyFilters}
          setSelectedFilters={setSelectedFilters}
          selectedFilters={selectedFilters}
          selectedDate={toDate}
          filtersData={filtersData}
          appliedFilters={appliedFilters}
          isPerformancePage={true}
        />
      )}
    </>
  );
};

export default PerformanceHeatmap;
